// Which microphone actually captured the SDR, and whether it was swapped mid-call.
//
// The SDK replaces the capture track in place when the input changes, without an event,
// so the caller re-reads the track and reports any new object here.

import { classifyInput, type InputKind } from './audio-devices';

export interface TrackLike {
  label?: string;
  getSettings?(): object;
}

interface MicTrack {
  current: TrackLike | null;
  label: string | null;
  labelEnd: string | null;
  ns: boolean | null;
  changes: number;
}

export function newMicTrack(): MicTrack {
  return { current: null, label: null, labelEnd: null, ns: null, changes: 0 };
}

export function trackAccepted(s: MicTrack, track: TrackLike | null): void {
  s.current = track;
  s.label = track?.label || null;
  try {
    const ns = (track?.getSettings?.() as Record<string, unknown> | undefined)?.[
      'noiseSuppression'
    ];
    s.ns = typeof ns === 'boolean' ? ns : null;
  } catch {
    s.ns = null;
  }
}

// Returns true when `track` is a different object from the one being watched.
export function trackSeen(s: MicTrack, track: TrackLike | null): boolean {
  if (!track || track === s.current) return false;
  s.current = track;
  s.changes += 1;
  return true;
}

export function trackEnd(s: MicTrack): void {
  s.labelEnd = s.current?.label || null;
}

export interface MicTrackReport {
  mic_track_label: string | null;
  mic_track_label_end: string | null;
  mic_track_kind: InputKind | null;
  // The track at the end against the picker at the end. Under the "Default - …"
  // pseudo-device the picker label is not the physical device, so both are compared.
  mic_track_label_match: 'selected' | 'resolved' | 'neither' | null;
  mic_track_ns: boolean | null;
  mic_track_changes: number;
}

export function reportTrack(
  s: MicTrack,
  selectedLabel: string | null,
  resolvedLabel: string | null,
): MicTrackReport {
  const label = s.label;
  const endLabel = s.labelEnd ?? label;
  return {
    mic_track_label: label,
    mic_track_label_end: s.labelEnd,
    mic_track_kind: label == null ? null : classifyInput(label),
    mic_track_label_match:
      endLabel == null
        ? null
        : endLabel === selectedLabel
          ? 'selected'
          : endLabel === resolvedLabel
            ? 'resolved'
            : 'neither',
    mic_track_ns: s.ns,
    mic_track_changes: s.changes,
  };
}
